// Recipe Module Phase 1 (docs/RECIPE_MODULE.md) — "run this recipe on the floor". When the recipe
// has a base ingredient (e.g. raw mushrooms cooked down into a sauce base), this asks for BOTH the
// real raw weight and the real cooked/yield weight — the yield loss is always derived from those
// two real numbers that run, never predicted in advance. The cooked weight drives every ingredient
// line's scaled quantity, same as before; the raw weight carries the base ingredient's own real
// purchase cost into the total. Recipes with no base ingredient keep the simpler single-input flow.
// Steps display in Spanish when the viewer's account language is set to Spanish (falls back to the
// original text if AI translation hasn't completed/failed) — staff don't have to wait on anyone to
// translate instructions for them.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import recipeService from '../services/recipeService';
import LotUsageRow from '../components/LotUsageRow';
import { useLanguage, t } from '../context/LanguageContext';

const LB_TO_KG = 0.453592;
const toKg = (value, unit) => {
  const n = parseFloat(value);
  if (isNaN(n)) return NaN;
  return unit === 'lb' ? n * LB_TO_KG : n;
};

// A plain .form-select reserves a good chunk of its own padding for the dropdown arrow — at a
// narrow width that padding alone can squeeze the option text itself out of view (looks like an
// empty chevron with no unit shown). Wide enough here to keep the text visible while matching the
// height of the plain .form-control number input it sits next to in the same input-group.
function UnitSelect({ value, onChange }) {
  return (
    <select className="form-select" style={{ width: 90, flex: '0 0 auto' }} value={value} onChange={e => onChange(e.target.value)}>
      <option value="kg">kg</option>
      <option value="lb">lb</option>
    </select>
  );
}

export default function PercentageRecipeRunPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [inputValue, setInputValue] = useState('');       // legacy single-input mode (no base ingredient)
  const [inputUnit, setInputUnit] = useState('kg');
  const [rawInputValue, setRawInputValue] = useState('');  // base-ingredient mode
  const [rawUnit, setRawUnit] = useState('kg');
  const [cookedInputValue, setCookedInputValue] = useState('');
  const [cookedUnit, setCookedUnit] = useState('kg');
  const [checkedSteps, setCheckedSteps] = useState({});
  const [notes, setNotes] = useState('');
  const [outputLotCode, setOutputLotCode] = useState('');
  const [lotUsagesMap, setLotUsagesMap] = useState({}); // ingredientId -> [{lotCode, quantityKg}]
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await recipeService.getPercentageRecipe(id);
      setRecipe(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load recipe.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = async () => {
    setShowHistory(true);
    try {
      const data = await recipeService.getPercentageRecipeExecutions(id);
      setHistory(Array.isArray(data) ? data : []);
    } catch { setHistory([]); }
  };

  const hasBaseIngredient = !!recipe?.baseIngredientId;

  // Always converted to kg here — the backend, every computed quantity, and the execution log all
  // work in kg regardless of which unit the user actually measured/typed in.
  const parsedRaw = toKg(rawInputValue, rawUnit);
  const parsedCooked = hasBaseIngredient ? toKg(cookedInputValue, cookedUnit) : toKg(inputValue, inputUnit);
  const validRaw = !hasBaseIngredient || (!!rawInputValue && !isNaN(parsedRaw) && parsedRaw > 0);
  const validCooked = !!parsedCooked && !isNaN(parsedCooked) && parsedCooked > 0
    && (hasBaseIngredient ? !!cookedInputValue : !!inputValue);
  const rawNotLessThanCooked = !hasBaseIngredient || !validRaw || !validCooked || parsedCooked <= parsedRaw;
  const validInput = validRaw && validCooked && rawNotLessThanCooked;

  const yieldLossPreview = useMemo(() => {
    if (!hasBaseIngredient || !validRaw || !validCooked || !rawNotLessThanCooked) return null;
    return ((parsedRaw - parsedCooked) / parsedRaw) * 100;
  }, [hasBaseIngredient, validRaw, validCooked, rawNotLessThanCooked, parsedRaw, parsedCooked]);

  // The base line's real measured contribution (cooked weight) represents its own % of the FINAL
  // batch — so the batch's total weight is derived from that, and every other line scales off the
  // derived total, not off the cooked weight directly (the cooked weight is only part of the
  // batch, same math as the backend's execute route).
  const computedLines = useMemo(() => {
    if (!recipe || !validInput) return [];
    const baseLine = hasBaseIngredient ? recipe.lines.find(l => l.isBaseIngredient) : null;
    const totalBatchWeight = baseLine ? parsedCooked / (baseLine.percentage / 100) : parsedCooked;
    return recipe.lines.map(l => ({
      ...l,
      quantityKg: l.isBaseIngredient ? parsedCooked : Number(((l.percentage / 100) * totalBatchWeight).toFixed(4)),
    }));
  }, [recipe, validInput, hasBaseIngredient, parsedCooked]);

  const totalBatchWeight = useMemo(
    () => computedLines.reduce((s, l) => s + l.quantityKg, 0),
    [computedLines]
  );

  const toggleStep = (stepId) => setCheckedSteps(prev => ({ ...prev, [stepId]: !prev[stepId] }));

  const allStepsChecked = recipe && recipe.steps.length > 0
    ? recipe.steps.every(s => checkedSteps[s.id])
    : true;

  const stepText = (s) => (lang === 'es' && s.instructionEs ? s.instructionEs : s.instruction);

  const handleSubmit = async () => {
    if (!validInput) return;
    setSubmitting(true);
    setError('');
    try {
      const lotUsages = Object.entries(lotUsagesMap).flatMap(([ingredientId, usages]) =>
        usages.map(u => ({ ingredientId: Number(ingredientId), lotCode: u.lotCode, quantityKg: Number(u.quantityKg) })));
      const payload = {
        checkedSteps: Object.keys(checkedSteps).filter(k => checkedSteps[k]).map(Number),
        notes: notes.trim() || null,
        outputLotCode: outputLotCode.trim() || undefined,
        lotUsages,
      };
      if (hasBaseIngredient) {
        payload.rawInputValue = parsedRaw;
        payload.cookedInputValue = parsedCooked;
      } else {
        payload.inputValue = parsedCooked;
      }
      const data = await recipeService.executePercentageRecipe(id, payload);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to log this run.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRunAgain = () => {
    setResult(null);
    setInputValue('');
    setRawInputValue('');
    setCookedInputValue('');
    setCheckedSteps({});
    setNotes('');
    setOutputLotCode('');
    setLotUsagesMap({});
  };

  if (loading) {
    return (
      <div className="container mt-4 text-center text-muted py-5">
        <div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}
      </div>
    );
  }

  if (error && !recipe) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  const CARD_STYLE = (color) => ({ border: '1px solid #e9ecef', borderTopWidth: 3, borderTopStyle: 'solid', borderTopColor: color });
  const SECTION_HEADER_STYLE = { fontSize: 11, letterSpacing: 1, fontWeight: 700 };

  return (
    <div className="container mt-4 pb-5">
      <div className="d-flex justify-content-between align-items-center mb-4 pb-3" style={{ borderBottom: '2px solid #f1f3f5' }}>
        <div>
          <h4 className="mb-1" style={{ color: '#198754' }}>▶ {recipe.name}</h4>
          {recipe.category && <small className="text-muted">{recipe.category}</small>}
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/recipes')}>
          {t('← Back to Library', '← Volver a la biblioteca', lang)}
        </button>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {result ? (
        <div className="card mb-3" style={CARD_STYLE('#198754')}>
          <div className="card-body">
            <div className="text-center mb-3">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
                style={{ width: 56, height: 56, background: '#d1f0dd', fontSize: 28 }}>
                ✅
              </div>
              <h6 className="text-success mb-0">{t('✓ Run logged', '✓ Preparación registrada', lang)}</h6>
            </div>
            {result.outputCredited && (
              <div className="alert alert-info py-2 mb-3">
                🏷️ {t('Credited to Inventory', 'Acreditado al Inventario', lang)} — {t('lot code', 'código de lote', lang)}: <strong>{result.outputLotCode}</strong>
              </div>
            )}
            {result.yieldLossPercent != null && (
              <div className="alert alert-info py-2 mb-3">
                {t('Yield loss', 'Pérdida de rendimiento', lang)}: <strong>{Number(result.yieldLossPercent).toFixed(1)}%</strong>
                {' '}({t('raw', 'crudo', lang)} {Number(result.rawInputValue).toFixed(2)} kg → {t('cooked', 'cocido', lang)} {Number(result.inputValue).toFixed(2)} kg)
              </div>
            )}
            <table className="table table-sm mb-3">
              <thead><tr><th>{t('Ingredient', 'Ingrediente', lang)}</th><th className="text-end">{t('Quantity', 'Cantidad', lang)}</th><th className="text-end">{t('Cost', 'Costo', lang)}</th></tr></thead>
              <tbody>
                {result.computedLines.map(l => (
                  <tr key={l.ingredientId} className={l.isBaseIngredient ? 'table-light fw-semibold' : ''}>
                    <td>{l.ingredientName}{l.isBaseIngredient && <span className="text-muted fw-normal ms-1">({t('raw', 'crudo', lang)})</span>}</td>
                    <td className="text-end">{l.quantityKg.toFixed(3)} kg</td>
                    <td className="text-end">{l.lineCost != null ? `$${l.lineCost.toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="table-light">
                  <td className="fw-bold">{t('Total batch', 'Lote total', lang)}</td>
                  <td className="text-end fw-bold">{result.totalBatchWeight != null ? `${Number(result.totalBatchWeight).toFixed(3)} kg` : '—'}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
            <div className="fw-semibold mb-3">
              {t('Total batch cost', 'Costo total del lote', lang)}: {result.totalCost != null ? `$${result.totalCost.toFixed(2)}` : '—'}
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={handleRunAgain}>{t('Run Again', 'Ejecutar de nuevo', lang)}</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/recipes')}>{t('Back to Library', 'Volver a la biblioteca', lang)}</button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="card mb-3" style={CARD_STYLE('#0d6efd')}>
            <div className="card-body">
              {hasBaseIngredient ? (
                <>
                  <div className="row g-3">
                    <div className="col-md-5">
                      <label className="form-label fw-semibold">
                        {lang === 'es' ? `Peso crudo de ${recipe.baseIngredientName}` : `Raw ${recipe.baseIngredientName} weight`} <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input type="number" step="0.01" className="form-control" value={rawInputValue}
                          onChange={e => setRawInputValue(e.target.value)}
                          placeholder={rawUnit === 'lb' ? 'e.g. 50' : 'e.g. 22.68'} autoFocus />
                        <UnitSelect value={rawUnit} onChange={setRawUnit} />
                      </div>
                    </div>
                    <div className="col-md-5">
                      <label className="form-label fw-semibold">
                        {recipe.yieldBasisLabel || t('Cooked weight', 'Peso cocido', lang)} <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input type="number" step="0.01" className="form-control" value={cookedInputValue}
                          onChange={e => setCookedInputValue(e.target.value)}
                          placeholder={cookedUnit === 'lb' ? 'e.g. 34.1' : 'e.g. 15.48'} />
                        <UnitSelect value={cookedUnit} onChange={setCookedUnit} />
                      </div>
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      {yieldLossPreview != null && (
                        <div className="text-center w-100">
                          <div className="text-muted" style={{ fontSize: 11 }}>{t('Yield loss', 'Pérdida', lang)}</div>
                          <div className="fw-semibold">{yieldLossPreview.toFixed(1)}%</div>
                        </div>
                      )}
                    </div>
                  </div>
                  {!rawNotLessThanCooked && (
                    <div className="form-text text-danger mt-1">
                      {t('Cooked weight cannot be more than the raw weight.', 'El peso cocido no puede ser mayor que el peso crudo.', lang)}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <label className="form-label fw-semibold">
                    {recipe.yieldBasisLabel || t('Yield / batch input', 'Rendimiento / lote', lang)} <span className="text-danger">*</span>
                  </label>
                  <div className="input-group" style={{ maxWidth: 260 }}>
                    <input type="number" step="0.01" className="form-control" value={inputValue}
                      onChange={e => setInputValue(e.target.value)}
                      placeholder={inputUnit === 'lb' ? 'e.g. 34.1' : 'e.g. 15.48'} autoFocus />
                    <UnitSelect value={inputUnit} onChange={setInputUnit} />
                  </div>
                </>
              )}
            </div>
          </div>

          {validInput && (
            <div className="card mb-3" style={CARD_STYLE('#198754')}>
              <div className="card-body">
                <div className="text-uppercase text-muted mb-2" style={SECTION_HEADER_STYLE}>⚖️ {t('Weigh out', 'Pesar', lang)}</div>
                <table className="table table-sm mb-0">
                  <thead className="table-light"><tr><th>{t('Ingredient', 'Ingrediente', lang)}</th><th className="text-end">%</th><th className="text-end">{t('Quantity', 'Cantidad', lang)}</th></tr></thead>
                  <tbody>
                    {computedLines.map(l => (
                      <tr key={l.ingredientId} className={l.isBaseIngredient ? 'table-light fw-semibold' : ''}>
                        <td>
                          {l.ingredientName}{l.isBaseIngredient && <span className="text-muted fw-normal ms-1">({t('cooked', 'cocido', lang)})</span>}
                          <LotUsageRow ingredientId={l.ingredientId}
                            expectedKg={l.isBaseIngredient ? parsedRaw : l.quantityKg}
                            onChange={usages => setLotUsagesMap(m => ({ ...m, [l.ingredientId]: usages }))} />
                        </td>
                        <td className="text-end text-muted">{l.percentage}%</td>
                        <td className="text-end fw-semibold">{l.quantityKg.toFixed(3)} kg</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-light">
                      <td className="fw-bold">{t('Total batch', 'Lote total', lang)}</td>
                      <td></td>
                      <td className="text-end fw-bold">{totalBatchWeight.toFixed(3)} kg</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {recipe.steps.length > 0 && (
            <div className="card mb-3" style={CARD_STYLE('#fd7e14')}>
              <div className="card-body">
                <div className="text-uppercase text-muted mb-2" style={SECTION_HEADER_STYLE}>📋 {t('Steps', 'Pasos', lang)}</div>
                {recipe.steps.map((s, idx) => (
                  <div key={s.id} className="form-check mb-2">
                    <input type="checkbox" className="form-check-input" id={`step-${s.id}`}
                      checked={!!checkedSteps[s.id]} onChange={() => toggleStep(s.id)} />
                    <label className="form-check-label" htmlFor={`step-${s.id}`}>
                      <span className="text-muted me-1">{idx + 1}.</span>{stepText(s)}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recipe.outputIngredientId && (
            <div className="card mb-3" style={CARD_STYLE('#0dcaf0')}>
              <div className="card-body">
                <div className="text-uppercase text-muted mb-2" style={SECTION_HEADER_STYLE}>🏷️ {t('Traceability', 'Trazabilidad', lang)}</div>
                <label className="form-label small fw-semibold">{t('Output Lot Code (optional)', 'Código de lote de salida (opcional)', lang)}</label>
                <input className="form-control form-control-sm" style={{ maxWidth: 280 }} value={outputLotCode}
                  onChange={e => setOutputLotCode(e.target.value)} placeholder={t('Leave blank to auto-generate', 'Dejar en blanco para generar automáticamente', lang)} />
                <div className="form-text" style={{ fontSize: 11 }}>
                  {t(`This run's output will be credited to "${recipe.outputIngredientName}" in Ingredient Inventory under this lot code.`,
                    `La salida de esta preparación se acreditará a "${recipe.outputIngredientName}" en el Inventario de Ingredientes bajo este código de lote.`, lang)}
                </div>
              </div>
            </div>
          )}

          <div className="card mb-3">
            <div className="card-body">
              <label className="form-label small fw-semibold">{t('Notes (optional)', 'Notas (opcional)', lang)}</label>
              <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
          </div>

          <button className="btn btn-success px-4" onClick={handleSubmit} disabled={!validInput || submitting || !allStepsChecked}>
            {submitting ? t('Logging…', 'Registrando…', lang) : t('✓ Log This Run', '✓ Registrar esta preparación', lang)}
          </button>
          {validInput && !allStepsChecked && (
            <div className="form-text text-warning mt-1">
              {t('Check off every step before logging this run.', 'Marque cada paso antes de registrar esta preparación.', lang)}
            </div>
          )}
        </>
      )}

      <hr className="my-4" />
      <button className="btn btn-link btn-sm p-0" onClick={loadHistory}>
        {showHistory ? t('Hide run history', 'Ocultar historial', lang) : t('Show run history', 'Mostrar historial', lang)}
      </button>
      {showHistory && (
        <div className="table-responsive mt-2">
          <table className="table table-sm">
            <thead className="table-light"><tr><th>{t('Date', 'Fecha', lang)}</th><th>{t('By', 'Por', lang)}</th><th className="text-end">{t('Input', 'Entrada', lang)}</th><th className="text-end">{t('Yield Loss', 'Pérdida', lang)}</th><th className="text-end">{t('Total Batch', 'Lote Total', lang)}</th><th>{t('Output Lot', 'Lote de Salida', lang)}</th><th>{t('Notes', 'Notas', lang)}</th></tr></thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan={7} className="text-muted text-center py-3">{t('No runs logged yet.', 'Aún no hay preparaciones registradas.', lang)}</td></tr>
              ) : history.map(h => (
                <tr key={h.id}>
                  <td>{new Date(h.executedAt).toLocaleString()}</td>
                  <td>{h.executedByName || '—'}</td>
                  <td className="text-end">
                    {h.rawInputValue != null ? `${h.rawInputValue} kg → ${h.inputValue} kg` : (h.inputValue != null ? `${h.inputValue} kg` : '—')}
                  </td>
                  <td className="text-end">{h.yieldLossPercent != null ? `${h.yieldLossPercent}%` : '—'}</td>
                  <td className="text-end fw-semibold">{h.totalBatchWeight != null ? `${h.totalBatchWeight} kg` : '—'}</td>
                  <td>{h.outputLotCode ? <span className="badge bg-light text-dark border">{h.outputLotCode}</span> : <span className="text-muted">—</span>}</td>
                  <td>{h.notes || <span className="text-muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
